import React from "react"
import { useSnackbar } from "notistack"
import { RoboComunidade } from "../../../services/apiRobo"
import { erroMsg } from "../components/comum"

type Sub = "vcards" | "suppliers" | "spammers" | "allowlist" | "optout"
const SUBS: Array<{ id: Sub; t: string }> = [
    { id: "vcards", t: "📇 vCards" },
    { id: "suppliers", t: "🏭 Fornecedores" },
    { id: "spammers", t: "🚫 Spammers" },
    { id: "allowlist", t: "✅ Sem análise" },
    { id: "optout", t: "🙅 Opt-out" },
]

/** Contatos (porte da rail 📞) com sub-abas. */
export function Contatos() {
    const [sub, setSub] = React.useState<Sub>("vcards")
    return (
        <section className="panel active" style={{ display: "block" }}>
            <div className="sub-tabs">
                {SUBS.map(s => (
                    <button
                        key={s.id}
                        className={"sub-tab" + (sub === s.id ? " active" : "")}
                        onClick={() => setSub(s.id)}
                    >
                        {s.t}
                    </button>
                ))}
            </div>
            <div style={{ padding: 12 }}>
                {sub === "vcards" && <Lista carregar={RoboComunidade.listarContatos} render={renderContato} vazio="Nenhum contato compartilhado." />}
                {sub === "suppliers" && (
                    <Lista
                        carregar={RoboComunidade.listarFornecedores}
                        render={(f: any) => `${f.nome || "—"}${f.categoria ? " · " + f.categoria : ""}${f.telefone ? " · " + f.telefone : ""}`}
                        remover={RoboComunidade.removerFornecedor}
                        vazio="Nenhum fornecedor."
                    />
                )}
                {sub === "spammers" && (
                    <Lista
                        carregar={RoboComunidade.listarSpammers}
                        render={(s: any) => `${s.nome || "—"} · ${s.waId || s.telefone || ""} · ${s.qtdSpam ?? 0} spam(s)`}
                        remover={RoboComunidade.removerSpammer}
                        vazio="Nenhum spammer registrado."
                    />
                )}
                {sub === "allowlist" && (
                    <ListaComAdd
                        carregar={RoboComunidade.listarAllowlist}
                        adicionar={wa => RoboComunidade.adicionarAllowlist({ waId: wa })}
                        remover={RoboComunidade.removerAllowlist}
                        render={(a: any) => `${a.nome || ""} ${a.waId}${a.nota ? " · " + a.nota : ""}`.trim()}
                        vazio="Nenhum contato sem análise."
                    />
                )}
                {sub === "optout" && (
                    <ListaComAdd
                        carregar={RoboComunidade.listarOptOut}
                        adicionar={wa => RoboComunidade.adicionarOptOut({ waId: wa })}
                        remover={RoboComunidade.removerOptOut}
                        render={(o: any) => `${o.nome || ""} ${o.waId}${o.motivo ? " · " + o.motivo : ""}`.trim()}
                        vazio="Ninguém em opt-out."
                    />
                )}
            </div>
        </section>
    )
}

function renderContato(c: any): string {
    return `${c.nome || "—"}${c.telefone ? " · " + c.telefone : ""}${c.organizacao ? " · " + c.organizacao : ""}`
}

function Lista({
    carregar,
    render,
    remover,
    vazio,
}: {
    carregar: () => Promise<any>
    render: (x: any) => string
    remover?: (id: number) => Promise<any>
    vazio: string
}) {
    const { enqueueSnackbar } = useSnackbar()
    const [itens, setItens] = React.useState<any[]>([])
    const [carregando, setCarregando] = React.useState(false)

    const load = React.useCallback(async () => {
        setCarregando(true)
        try {
            const r = await carregar()
            setItens(Array.isArray(r) ? r : r?.items || [])
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        } finally {
            setCarregando(false)
        }
    }, [carregar, enqueueSnackbar])

    React.useEffect(() => {
        load()
    }, [load])

    return (
        <>
            {carregando && <div className="muted small">Carregando…</div>}
            {!carregando && itens.length === 0 && <div className="empty">{vazio}</div>}
            <ul className="list" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {itens.map(x => (
                    <li
                        key={x.id}
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 0",
                            borderBottom: "1px solid var(--border)",
                        }}
                    >
                        <span className="small">{render(x)}</span>
                        {remover && (
                            <button
                                className="ghost warning"
                                onClick={async () => {
                                    try {
                                        await remover(x.id)
                                        enqueueSnackbar("Removido.", { variant: "success" })
                                        load()
                                    } catch (e) {
                                        enqueueSnackbar(erroMsg(e), { variant: "error" })
                                    }
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </li>
                ))}
            </ul>
        </>
    )
}

function ListaComAdd(props: {
    carregar: () => Promise<any>
    adicionar: (wa: string) => Promise<any>
    remover: (id: number) => Promise<any>
    render: (x: any) => string
    vazio: string
}) {
    const { enqueueSnackbar } = useSnackbar()
    const [wa, setWa] = React.useState("")
    const [nonce, setNonce] = React.useState(0)

    async function add() {
        if (!wa.trim()) return
        try {
            await props.adicionar(wa.trim())
            setWa("")
            setNonce(n => n + 1)
            enqueueSnackbar("Adicionado.", { variant: "success" })
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        }
    }

    return (
        <>
            <div className="form-row" style={{ gap: 6, marginBottom: 8 }}>
                <input
                    type="text"
                    placeholder="WhatsApp (só números) ou LID"
                    value={wa}
                    onChange={e => setWa(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && add()}
                />
                <button onClick={add} disabled={!wa.trim()}>
                    ➕ Adicionar
                </button>
            </div>
            <Lista key={nonce} carregar={props.carregar} render={props.render} remover={props.remover} vazio={props.vazio} />
        </>
    )
}

export default Contatos
